import { useQuery } from "@tanstack/react-query";
import { authService } from "@/services/authService";
import axiosInstance from "@/services/api";
import { User, Organization } from "types";

const getErrorStatus = (error: unknown): number | undefined =>
  (error as { response?: { status?: number } }).response?.status ??
  (error as { statusCode?: number }).statusCode ??
  (error as { status?: number }).status;

// A fresh LTI transfer token can be briefly rejected with a 401 right after
// login. Retry a few times with a short backoff so the session loads once the
// token settles, instead of giving up on the first 401.
//
// [MED-6943] Temporary debug: logs every 401 retry decision with its attempt
// count so we can confirm the session `user` query rides out the transfer-token
// race (failureCount climbing past 1, then resolving) instead of dying on the
// first retry. Remove once verified in the merged build.
const makeRetryOn401 =
  (label: string) =>
  (failureCount: number, error: unknown): boolean => {
    const status = getErrorStatus(error);
    const willRetry = status === 401 && failureCount < 4;
    console.debug(`[MED-6943] ${label} query error`, {
      status,
      failureCount,
      willRetry,
    });
    return willRetry;
  };

const retryOn401Delay = (failureCount: number): number =>
  Math.min(300 * 2 ** failureCount, 2000);

/**
 * Fetches user data from /api/users/me
 * Uses token presence as source of truth for authentication
 * Uses React Query directly instead of Refine hooks to avoid dependency on RefineWrapper
 */
export const useUserQuery = () => {
  const token = authService.getToken();
  const hasValidToken = Boolean(token?.access_token);

  return useQuery({
    queryKey: ["session", "user"],
    queryFn: async () => {
      const response = await axiosInstance.get<User>("/api/users/me");
      return response.data;
    },
    retry: makeRetryOn401("user"),
    retryDelay: retryOn401Delay,
    // [MED-6943] Temporary debug — remove once verified in the merged build.
    onSuccess: () => console.debug("[MED-6943] user query resolved"),
    onError: () =>
      console.debug("[MED-6943] user query gave up (still failing)"),
    enabled: hasValidToken, // Re-enabled with token check
    staleTime: Infinity, // Never mark as stale — invalidate explicitly
    cacheTime: 1000 * 60 * 10, // 10 minutes (v4 API name; renamed to gcTime in v5)
  });
};

/**
 * Fetches organizations from /api/users/me/organizations
 * Uses React Query directly instead of Refine hooks to avoid dependency on RefineWrapper
 */
export const useOrganizationsQuery = () => {
  const token = authService.getToken();
  const hasValidToken = Boolean(token?.access_token);

  return useQuery({
    queryKey: ["session", "organizations"],
    queryFn: async () => {
      const response = await axiosInstance.get<Organization[]>(
        "/api/users/me/organizations"
      );
      return response.data;
    },
    retry: makeRetryOn401("organizations"),
    retryDelay: retryOn401Delay,
    // [MED-6943] Temporary debug — remove once verified in the merged build.
    onSuccess: () => console.debug("[MED-6943] organizations query resolved"),
    onError: () =>
      console.debug("[MED-6943] organizations query gave up (still failing)"),
    enabled: hasValidToken,
    staleTime: Infinity, // Never mark as stale — invalidate explicitly
    cacheTime: 1000 * 60 * 10, // 10 minutes (v4 API name; renamed to gcTime in v5)
  });
};
